export enum FormatCode {
  LONG_DATE = 'L',
  SHORT_DATE = 'S',
  NUMBER = 'N',
  CURRENCY = 'C',
  TIME = 'T',
  CUSTOM = '*',
  CUSTOM_ICU = 'i',
  CUSTOM_NUMBER = 'n',
  CUSTOM_CURRENCY = 'c',
  CUSTOM_SCIENTIFIC = 'e',
  CUSTOM_PERCENTAGE = 'p',
}

/**
 * Returns the maximum precision a metric with the given `formatString` will display.
 *
 * `0.01` by default, `0.0001` in the case of percentages.
 *
 * @param formatString
 */
export const maxPrecision = (formatString?: string): number => {
  const formatInfo = parseFormatString(formatString);
  return (formatInfo.dataType === FormatDataType.FORMAT_NUMBER && formatInfo.isPercentage && 0.0001) || 0.01;
};

/**
 * Returns whether or not the `formatString` is a percentage.
 * @param formatString
 */
export const isPercentageFormat = (formatString?: string): boolean => {
  const formatInfo = parseFormatString(formatString);
  return formatInfo.dataType === FormatDataType.FORMAT_NUMBER && formatInfo.isPercentage;
};

export enum FormatDataType {
  FORMAT_NUMBER = 'FORMAT_NUMBER',
  FORMAT_DATE_TIME = 'FORMAT_DATE_TIME',
  FORMAT_AUTO = 'FORMAT_AUTO',
}

// This is currently a subset of the information we need to fully format a number.  If we end up doing the full
// format to support locales, we will need to add the following:
// decimal places, separators t/f, decimal char, separator char.  Possibly grouping spec, units (kmb)
export interface NumberFormatInfo {
  readonly dataType: FormatDataType.FORMAT_NUMBER;
  readonly isPercentage: boolean;
  readonly isCurrency: boolean;
  readonly prefixSymbol: string;
  readonly postfixSymbol: string;
}

export interface DateTimeFormatInfo {
  readonly dataType: FormatDataType.FORMAT_DATE_TIME;
}

export interface AutoFormatInfo {
  readonly dataType: FormatDataType.FORMAT_AUTO;
}

export type FormatInfo = NumberFormatInfo | DateTimeFormatInfo | AutoFormatInfo;

const autoFormat: AutoFormatInfo = { dataType: FormatDataType.FORMAT_AUTO };

/**
 * This function takes a format string and parses it into formatting components.
 *
 * @export
 * @param {string} formatString
 * @returns {FormatInfo}
 */
export function parseFormatString(formatString?: string): FormatInfo {
  if (!formatString || formatString.length === 0) {
    return autoFormat;
  }

  const tokenizer = new Tokenizer(formatString);

  // TODO: TFSID 1006678: Support non-US-English locales.

  // Look at the first character of the string to determine the format type.
  switch (tokenizer.next()) {
    case FormatCode.CURRENCY:
      return parseCurrency(tokenizer);
    case FormatCode.CUSTOM_CURRENCY:
      return parseCustomCurrency(tokenizer);
    case FormatCode.CUSTOM_NUMBER:
      return parseCustomNumber(tokenizer);
    case FormatCode.CUSTOM_PERCENTAGE:
      return parseCustomPercentage(tokenizer);
    case FormatCode.CUSTOM:
      return parseCustom(tokenizer);
    default:
      return autoFormat;
  }
}

/**
 * A currency format string looks like 'C1033%'.  The numbers in the middle are the locale ID
 * of the currency symbol to use.  So 1033 = en_US = '$'.
 * @param tokenizer the format string to be parsed
 */
function parseCurrency(tokenizer: Tokenizer): NumberFormatInfo {
  let prefixSymbol = '';
  let postfixSymbol = '';

  const lcid = extractLcid(tokenizer);

  switch (lcid) {
    case 1033:
      prefixSymbol = '$';
      break;
    case 1031:
      postfixSymbol = '€';
      break;
    default:
      break;
  }
  return {
    dataType: FormatDataType.FORMAT_NUMBER,
    isPercentage: false,
    isCurrency: true,
    prefixSymbol,
    postfixSymbol,
  };
}

/**
 * Custom currency format strings look like 'c"$"##0.00;"$"(##0.00)'.
 * @param tokenizer the format string to be parsed
 */
function parseCustomCurrency(tokenizer: Tokenizer): NumberFormatInfo {
  const symbols = parsePrefixPostFixSymbols(tokenizer);

  return {
    dataType: FormatDataType.FORMAT_NUMBER,
    isPercentage: false,
    isCurrency: true,
    prefixSymbol: symbols[0],
    postfixSymbol: symbols[1],
  };
}

/**
 * Custom number format strings look like 'n##0.00"%";-##0.00"%"'.
 * @param tokenizer the format string to be parsed
 */
function parseCustomNumber(tokenizer: Tokenizer): NumberFormatInfo {
  const symbols = parsePrefixPostFixSymbols(tokenizer);

  // If the format string contains a '%' we still just want to format it as a regular number.
  // Identifying it as a percentage format would cause the number to get multiplied by 100.
  return {
    dataType: FormatDataType.FORMAT_NUMBER,
    isPercentage: false,
    isCurrency: false,
    prefixSymbol: symbols[0],
    postfixSymbol: symbols[1],
  };
}

/**
 * Custom format strings look like '*##0.00"%";-##0.00"%"'.
 * @param tokenizer the format string to be parsed
 */
function parseCustom(tokenizer: Tokenizer): NumberFormatInfo {
  // The '*' format is a user-specified format template.  It could potentially be a date format.
  // Since we are formatting metric values here, we are going to assume it conforms to the number
  // formatting layouts, and just treat it as a custom number format.
  return parseCustomNumber(tokenizer);
}

function parsePrefixPostFixSymbols(tokenizer: Tokenizer): readonly [string, string] {
  let prefixSymbol = '';
  let postfixSymbol = '';

  let cur = tokenizer.next();

  // Check for a currency symbol prefix.
  if (cur === '"') {
    cur = tokenizer.next();
    while (cur && cur !== '"') {
      prefixSymbol += cur;
      cur = tokenizer.next();
    }
    cur = tokenizer.next();
  }

  // For now skip over the number part.
  while (cur && cur !== ';') {
    // Check for a currency symbol postfix.
    if (cur === '"') {
      cur = tokenizer.next();
      while (cur && cur !== '"') {
        postfixSymbol += cur;
        cur = tokenizer.next();
      }
    }
    cur = tokenizer.next();
  }

  return [prefixSymbol, postfixSymbol];
}

/**
 * The custom percentage string looks like 'p0.00%'.
 * @param tokenizer format string to be parsed
 */
function parseCustomPercentage(tokenizer: Tokenizer): NumberFormatInfo {
  // Since we aren't parsing the decimal places yet, just return the format info.
  return {
    dataType: FormatDataType.FORMAT_NUMBER,
    isPercentage: true,
    isCurrency: false,
    prefixSymbol: '',
    postfixSymbol: '',
  };
}

function extractLcid(tokenizer: Tokenizer): number {
  let lcid = '';
  let cur = tokenizer.next();

  while (cur && cur !== '%') {
    lcid += cur;
    cur = tokenizer.next();
  }

  return +lcid;
}

class Tokenizer {
  private readonly formatString: string;
  private readonly len: number;

  private pos: number = 0;

  public constructor(formatString: string) {
    this.formatString = formatString;
    this.len = formatString.length || 0;
  }

  public next(): string {
    if (this.pos === this.len) {
      return '';
    }
    return this.formatString.charAt(this.pos++);
  }
}
